import { Injectable } from '@angular/core';

import { catchError, first, firstValueFrom, interval, of, tap, timeout } from 'rxjs';

import {
    CollageProcess,
    JOB_STATUS_CANCELLED,
    JOB_STATUS_ERROR,
    JOB_STATUS_FINISHED,
    JOB_STATUS_PROCESSING,
    JOB_STATUS_UPLOADING,
    Job,
    JobStatus,
    Process,
    ResultPage,
} from '@mirage-projects/cw-models';
import { DataAccessPcvizService, DbPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { CatalogService, ProcessService } from '../../abstract-classes';
import { JobService } from '../../abstract-classes/job.service';
import { LogService } from '../log/log.service';
import { TranslationService } from '../translate/translation.service';

const translatedJobs: { [key: string]: number } = {
    IMPORT_REFERENCES_TO_SITES: Process.IMPORT_REFERENCES_TO_SITES,
    GENERATE_SNAPSHOT: Process.GENERATE_SNAPSHOT,
    EXPORT_PANORAMA: Process.EXPORT_PANORAMA,
    VOLUME_CALCULATION: Process.VOLUME_CALCULATION,
    PUBLISH_TO_CW: Process.PUBLISH_TO_CW,
    ASSISTED_TARGET_DETECTION: Process.ASSISTED_TARGET_DETECTION,
};
const errorStatusArray: JobStatus[] = ['CANCELLING', 'INCOMPLETE', 'STARTING', 'CANCELLED', 'FAILURE'];

export interface JobProgressInfo {
    progress: number;
    statusMessage: string | null;
    jobTree?: Partial<Job>[];
    jobId?: number;
}

export interface JobResult {
    mwFilters: string;
    createdLayers: number[][];
    'download-uri'?: string;
}

/**
 * this service manage the usabillity of the jobs
 */
@Injectable({ providedIn: 'root' })
export class JobsService {
    readonly cancellingStatus: JobStatus = 'CANCELLING';

    private jobTranslatedNames: Map<Job, string> = new Map();

    constructor(
        private baseJobService: JobService,
        private translationService: TranslationService,
        private catalogService: CatalogService,
        private logService: LogService,
        private dbPcvizService: DbPcvizService,
        private dataAccessPcvizService: DataAccessPcvizService,
        private jobPcvizService: JobPcvizService,
        private processService: ProcessService,
    ) {}

    /**
     * Gets a job from the backend from its id
     * @param id Job's id
     */
    async getJob(id: string): Promise<Job> {
        return firstValueFrom(this.baseJobService.get(id));
    }

    /**
     * cancels the current downloading job
     * @param job job to cancel
     */
    async cancel(job: Job): Promise<void> {
        if (this.isJobUploading(job)) {
            job.uploadJob.cancel?.();

            if (job.status !== 'QUEUED') {
                // A timeout inside the uploadJob.cancel changes the value to incomplete, we wait for it
                await firstValueFrom(
                    interval(250).pipe(
                        tap(() => this.ensureUploadJob(job)),
                        first(() => !!(job.status === 'INCOMPLETE' && job.uploadJob.status === 'INCOMPLETE')),
                        // just in case
                        timeout(10000),
                        catchError(() => {
                            this.logService.warn('Upload cancel timeout without INCOMPLETE status');
                            return of(null);
                        })
                    )
                );

                return;
            } else {
                this.dbPcvizService.getControllerDB('Upload').removeUploadJob(job.uploadJob);
            }
        }
        await this.dataAccessPcvizService.getDriver('da.job').cancel(job.id);
        job.status = this.cancellingStatus;
    }

    /**
     * Pauses the job
     * @param job Job to pause
     */
    pause(job: Job): void {
        if (!job.uploadJob?.pause) {
            return;
        }

        job.uploadJob.pause();
        this.ensureUploadJob(job);
    }

    /**
     * Resumes a previously paused job
     * @param job Job to pause
     */
    resume(job: Job): void {
        if (!job.uploadJob?.resume) {
            return;
        }

        job.uploadJob.resume();
        this.ensureUploadJob(job);
    }

    /**
     * Gets the detailed information about job progress state
     * @param id Job id
     */
    async getJobProgressWithDetail(id: number): Promise<JobProgressInfo> {
        return (await this.getJobProgressInfo(id, true)) as unknown as Promise<JobProgressInfo>;
    }

    async getJobProgressWithDetailByProcessUuid(processUuid: string): Promise<JobProgressInfo> {
        return await this.getJobProgressInfoByProcessUuid(processUuid);
    }

    parseJobProgressWithDetail(job: Job | CollageProcess): JobProgressInfo {
        // Currently there are jobs without jobTree (Collage Process)
        return {
            // When job is a Collage Process, it is not necessary to parse their progress (because it is a number or null)
            progress: 'jobTree' in job ? this.parseJobProgress(job) : (job.progress ?? 0),
            statusMessage: 'statusMessage' in job ? this.parseStatusMessage(job) : null,
            // When job is a Collage Process, is does not have jobTree and it is not necessary to parse it
            jobTree: 'jobTree' in job ? this.parseJobTree(job) : [],
            jobId: 'id' in job ? job.id : undefined,
        };
    }

    parseJobProgress(job: Job): number {
        if (!job.progress && !job.jobTree?.length) {
            return 0;
        }
        if (job.status === 'SUCCESS') {
            return 100;
        }

        if (job.jobTree.length) {
            const sum = job.jobTree.reduce((acc, job) => acc + this.parseJobProgress(job), 0);

            return sum / job.jobTree.length;
        }

        const progress = JSON.parse(job.progress as string);

        return (progress.value / (progress.max - progress.min)) * 100;
    }

    parseJobTree(job: Job): Partial<Job>[] {
        const process = this.jobPcvizService.getJobProcess().getKeyById;

        const parseJobs = job.jobTree.map((element: any) => ({
            process: `JOBS.NAME_${process(element.process)}`,
            processId: element.process,
            status: element.status,
            progress: this.parseJobProgress(element),
            statusMessage: this.parseStatusMessage(element),
        })) as any;

        return parseJobs.reverse();
    }

    parseStatusMessage(job: Job): string | null {
        let jsonMessage = job.statusMessage;
        if (!jsonMessage && job.jobTree.length) {
            let id = job.jobTree[0].id;
            jsonMessage = job.jobTree[0].statusMessage;

            job.jobTree.forEach((element) => {
                if (element.id <= id) {
                    id = element.id;
                    if (element.status === 'STARTED' || element.status === 'FAILURE') {
                        jsonMessage = element.statusMessage;
                    }
                }
            });
        }

        const isJsonMessageString = typeof jsonMessage === 'string';
        try {
            if (isJsonMessageString) {
                return `JOBS.CODE_${JSON.parse(jsonMessage as string).code}`;
            }
            throw new Error('Wrong statusMessage format');
        } catch {
            if (job.statusMessage?.includes('Exception trying to connect to the processing system')) {
                return 'JOBS.CODE_ERROR_BEFORE_LAUNCH';
            }
            return job.statusMessage;
        }
    }

    /* istanbul ignore next */
    /**
     * Checks if a passed job is currently being uploaded
     * @param job Job to check
     */
    isJobUploading(job: Job): boolean {
        return JOB_STATUS_UPLOADING.includes(job.status);
    }

    /* istanbul ignore next */
    /**
     * Checks if a passed job is currently being processed
     * @param job Job to check
     */
    isJobProcessing(job: Job): boolean {
        return JOB_STATUS_PROCESSING.includes(job.status);
    }

    /* istanbul ignore next */
    /**
     * Checks if a passed job is currently finished
     * @param job Job to check
     */
    isJobFinished(job: Job): boolean {
        return JOB_STATUS_FINISHED.includes(job.status);
    }

    getJobTotalCost(job: Job): number | null {
        // no cost yet
        if (!this.isJobFinished(job)) {
            return -1;
        }

        if (job.supportUser) {
            return null;
        }

        if (job.jobTree?.length) {
            return job.jobTree?.map((item) => item.totalCost).reduce((total, prevCost) => total + parseFloat(prevCost ?? '0'), 0);
        }

        return parseFloat(job.totalCost ?? '0');
    }

    async getJobResult(id: number): Promise<JobResult | undefined> {
        const job = await firstValueFrom(this.baseJobService.get(id.toString()));

        return job.result ? JSON.parse(job.result) : undefined;
    }

    ensureUploadJob(job: Job): void {
        const uploadJob = this.dbPcvizService.getControllerDB('Upload').jobExistsId(job);
        if (!uploadJob) {
            return;
        }
        job.uploadJob = uploadJob;

        // We overwrite the status only if it is PENDING
        if (job.status === 'PENDING') {
            job.status = uploadJob.status;
        }
    }

    getJobName(job: Job): string {
        return this.jobTranslatedNames.get(job) ?? job.name;
    }

    clearJobNames(): void {
        this.jobTranslatedNames.clear();
    }

    /**
     * Gets the job object data
     * @param job Job to get the data from
     * @param fromMe Flag that indicates it's calling recursively this method
     */
    async retrieveObject(job: Job, fromMe = false): Promise<void> {
        if (job.jobTree?.length) {
            return;
        }

        let name: string;
        if (Object.values(translatedJobs).includes(job.process)) {
            const label = Object.keys(translatedJobs).find((key) => translatedJobs[key] === job.process);
            const processName = `JOBS.${label}`;
            name = `${job.name} - ${this.translationService.translate(processName).toLowerCase()}`;
            this.jobTranslatedNames.set(job, name);
            return;
        }

        if (!job.name && !job.output) {
            if (errorStatusArray.includes(job.status) || fromMe) {
                name = this.translationService.translate(`JOBS.ERROR_${job.status}`);
                this.jobTranslatedNames.set(job, name);
                return;
            }

            window.setTimeout(async () => {
                await this.retrieveObject(job, true);
            }, 1000);

            return;
        }

        try {
            const response = await firstValueFrom(this.catalogService.getCatalogObject(job.output));
            job.object ??= response;

            if (!job.name) {
                name = job.object.name;
                this.jobTranslatedNames.set(job, name);
            }
        } catch (e: any) {
            const isProject = job.output.includes('project');
            const errorName = this.getErrorNameByObject(e.status, isProject);

            name = this.translationService.translate(errorName);
            this.jobTranslatedNames.set(job, name);
        }
    }

    /**
     * Fill and parses different job attributes
     * @param resultPage Results as server response
     */
    adaptJobsList(resultPage: ResultPage<Job>): void {
        resultPage.results.forEach((job) => {
            this.ensureUploadJob(job);
            if (job.jobTree.length) {
                return;
            }

            const input = JSON.parse(job.inputData);

            const files: string[] = input.files;
            const sizes: string[] | null = input.fileSizes ?? null;
            if (files) {
                job.files = [];
                files.forEach((fileName, index) => {
                    if (typeof fileName !== 'string') {
                        return;
                    }
                    job.files.push({ name: fileName.substring(fileName.indexOf('/') + 1), size: Number.parseInt(sizes?.[index] ?? '0') });
                });
            }
        });
    }

    private async getJobProgressInfo(id: number, withDetail: boolean): Promise<JobProgressInfo | number> {
        const jobs$ = this.baseJobService.get(id.toString());

        const job = await firstValueFrom(jobs$);
        const errorOrCancelledJob = [...JOB_STATUS_ERROR, ...JOB_STATUS_CANCELLED].includes(job.status);
        if (errorOrCancelledJob) {
            return Promise.reject({ id: id, status: job.status });
        }

        return withDetail ? this.parseJobProgressWithDetail(job) : this.parseJobProgress(job);
    }

    private async getJobProgressInfoByProcessUuid(processUuid: string): Promise<JobProgressInfo> {
        const jobs$ = this.processService.getByExternal(processUuid);

        const job = await firstValueFrom(jobs$);
        const errorJob = ['FAILED'].includes(job.status);
        if (errorJob) {
            return Promise.reject({ processUuid: processUuid, status: job.status });
        }

        return this.parseJobProgressWithDetail(job);
    }

    private getErrorNameByObject(status: number, isProject = true): string {
        const label = isProject ? 'PROJECT' : 'LAYER';

        return status === 404 ? `JOBS.${label}_NOT_FOUND` : `JOBS.${label}_ERROR_UNKNOWN`;
    }
}
